import { SIP_CONFIG } from '@/config/sipConfig';
import JsSIP from 'jssip';

import { RTCSession } from 'jssip/lib/RTCSession';
import { iceServers } from './iceServers';
import { TCallStatus } from '../sip-recoil/sipRecoil';
import { notification } from 'antd';
import { formatPhoneNumber } from '@/shared/util/format/phoneNumberFormatUtil';

JsSIP.debug.enable('JsSIP:*');
// JsSIP.debug.disable();

let userAgent: JsSIP.UA | null = null;
let currentSession: RTCSession | null = null; // 현재 활성화된 세션
const remoteStream = new MediaStream();

// 코덱 제한 함수 (G.711 코덱만 허용)
const limitToG711 = (sdp: string): string => {
  return sdp.replace(/(m=audio \d+ .+?)(\r\n)/g, (match, p1, p2) => `${p1} PCMU${p2}`);
};

// SIP 클라이언트 초기화
export const initializeSipClient = (
  data: any,
  setSipStatus: React.Dispatch<React.SetStateAction<'connected' | 'disconnected' | null>>,
  setIncomingCallInfo: React.Dispatch<React.SetStateAction<{ caller: string } | null>>,
  setCallStatus: React.Dispatch<React.SetStateAction<TCallStatus>>,
  callStatus: TCallStatus,
) => {
  if (userAgent) {
    setSipStatus('connected');
    console.error('SIP Client is already initialized');
    return;
  }

  const socket = new JsSIP.WebSocketInterface(SIP_CONFIG.server as string);
  const configuration = {
    sockets: [socket],
    uri: `sip:${data.username}@voip.seoultel.co.kr`,
    username: data.username,
    password: data.password,
    trace_sip: SIP_CONFIG.trace_sip,
  };

  userAgent = new JsSIP.UA(configuration);

  // 레지스터 연결 시작
  userAgent.on('registered', () => {
    setSipStatus('connected');
    // notification.success({
    //   message: '번호 연결 성공',
    //   description: `${formatPhoneNumber(data.username)} 연결되었습니다.`,
    //   placement: 'topRight', // 우측 상단에 표시
    //   duration: 3,
    // });
  });

  // 레지스터 연결 끊기
  userAgent.on('unregistered', () => {});

  // 레지스터 연결 실패
  userAgent.on('registrationFailed', (e) => {
    console.error('SIP 등록 실패:', e.cause);
    setSipStatus('disconnected');
  });

  // 레지스터 연결 종료
  userAgent.on('disconnected', () => {
    setSipStatus('disconnected');
    // notification.info({
    //   message: '번호 연결 종료',
    //   description: `${formatPhoneNumber(data.username)} 연결 해제되었습니다.\n수신 ARS로 대체됩니다.`,
    //   placement: 'topRight', // 우측 상단에 표시
    //   duration: 3,
    //   style: { whiteSpace: 'pre-line' }, // 줄바꿈 적용
    // });
  });

  // RTC연결
  userAgent.on('newRTCSession', (e: any) => {
    const { session } = e;

    // 현재 활성화된 세션이 있는지 확인
    if (currentSession) {
      // 수신자 정보
      const incomUser = session.remote_identity.uri.user;

      // 새 세션을 거절 (486: Busy Here)
      session.terminate({
        status_code: 486,
        reason_phrase: `caller : ${incomUser ? incomUser : 'Busy'}`,
      });

      // 사용자에게 알림
      notification.warning({
        message: incomUser ? incomUser : '새로운 전화 수신',
        description: incomUser ? '수신 전화가 왔습니다.' : '이미 통화 중입니다. 기존 통화를 종료하고 다시 시도하세요.',
        placement: 'topRight',
        duration: 3,
      });

      return;
    }

    currentSession = session; // 현재 세션 저장

    session.on('failed', (e: any) => {
      currentSession = null; // 세션 종료 시 초기화
      setCallStatus('ended');
    });

    session.on('ended', () => {
      currentSession = null; // 세션 종료 시 초기화
      setCallStatus('ended');
    });

    // 수신 전화인 경우
    if (session.direction === 'incoming') {
      setCallStatus('ringing'); // 수신 전화 상태로 변경
      setIncomingCallInfo({ caller: session.remote_identity.uri.user }); // 수신자 정보 설정
    }
    handleSessionEvents(session);
  });

  userAgent.on('sipEvent', (e: any) => {
    if (e.request.method === 'OPTIONS') {
      const response = e.request.createResponse(200, 'OK');
      e.request.reply(response);
    }
  });

  userAgent.start();
};

// 세션 이벤트 처리
const handleSessionEvents = (session: any) => {
  const peerConnection = session.connection;

  // peerConnection이 null인 경우 에러 방지
  if (!peerConnection) {
    console.error('PeerConnection is not initialized');
    return;
  }

  // ICE 후보자 이벤트 리스너
  peerConnection.onicecandidate = (event: any) => {
    if (event.candidate) {
      const candidateStr = event.candidate.candidate;
      const candidateObj = event.candidate;

      console.log('🌐 ICE 후보자 전체:', candidateObj);

      if (candidateStr.includes('typ relay')) {
        console.log('✅ TURN 후보자');
        console.log('📍 후보자 문자열:', candidateStr);
        console.log('➡️ 관련 TURN 주소 추정:', extractTurnInfo(candidateStr));
      } else if (candidateStr.includes('typ srflx')) {
        console.log('🌍 STUN 후보자:', candidateStr);
      } else if (candidateStr.includes('typ host')) {
        console.log('🏠 로컬 후보자:', candidateStr);
      }
    } else {
      console.log('✅ ICE 후보자 수집 완료');
    }
  };
  // 문자열에서 TURN 주소를 추출하는 보조 함수
  const extractTurnInfo = (candidate: string): string | null => {
    const parts = candidate.split(' ');
    const ip = parts[4];
    const port = parts[5];
    const protocol = parts[2];

    return ip && port ? `${protocol}://${ip}:${port}` : null;
  };

  // ICE 연결 상태 변경 이벤트 리스너
  peerConnection.oniceconnectionstatechange = () => {
    if (peerConnection.iceConnectionState === 'failed') {
      console.error('ICE 연결 실패. 3초 후 재시도...');
      setTimeout(() => peerConnection.restartIce(), 3000);
    }
  };

  // 트랙 이벤트 리스너 (오디오만 처리)
  peerConnection.ontrack = (event: any) => {
    event.streams[0].getTracks().forEach((track: any) => {
      remoteStream.addTrack(track);
    });

    const remoteAudio = document.getElementById('remoteAudio') as HTMLAudioElement;
    if (remoteAudio) {
      remoteAudio.srcObject = remoteStream;
      remoteAudio.play().catch((err) => console.error('오디오 재생 오류:', err));
    }
  };
};

// 발신 전화 처리
export const makeCall = async (target: string, setCallStatus: React.Dispatch<React.SetStateAction<TCallStatus>>) => {
  if (!userAgent) {
    console.error('SIP userAgent가 초기화되지 않았습니다.');
    return;
  }

  const eventHandlers = {
    progress: () => {
      setCallStatus('dialing');
    },
    failed: (e: any) => {
      notification.warning({
        message: `${e.cause}`,
        description: '스피커, 마이크 연결(설정)을 확인해 주세요.',
        placement: 'topRight', // 우측 상단에 표시
        duration: 2,
        style: { whiteSpace: 'pre-line' }, // 줄바꿈 적용
      });
      setCallStatus('idle');
    },
    ended: (e: any) => {
      console.log('통화 종료 원인:', e.cause);
      setCallStatus('idle');
    },
    confirmed: () => {
      setCallStatus('connected');
    },
  };

  const options = {
    eventHandlers,
    mediaConstraints: { audio: true, video: false }, // 오디오만 활성화
    pcConfig: {
      iceServers,
      iceCandidatePoolSize: 4, // 미리 4개의 후보를 수집하여 빠르게 연결
    }, // WebRTC 설정
  };

  try {
    const session = userAgent.call(`sip:${target}@voip.seoultel.co.kr`, options);
    currentSession = session; // 현재 세션 저장

    // WebRTC 연결 확인
    if (session.connection) {
      console.log('WebRTC 연결 성공');
    } else {
      console.error('WebRTC 연결 실패');
    }
  } catch (error) {
    console.error('통화 설정 중 오류 발생:', error);
    setCallStatus('idle');
  }
};

// 모든 호출 종료
export const endCall = () => {
  if (userAgent) {
    userAgent.terminateSessions();
    currentSession = null; // 세션 초기화
    console.log('모든 통화 종료됨');
  }
};

// SIP 클라이언트 종료
export const closeSipClient = () => {
  if (userAgent) {
    userAgent.stop();
    userAgent = null;
    currentSession = null; // 세션 초기화
    console.log('SIP 클라이언트 종료됨');
  }
};

// 현재 활성화된 세션 반환
export const getCurrentSession = () => {
  return currentSession;
};
